/// <amd-module name="VSS/Features/Monaco/Languages" />
import { IFilesHubViewService, IGitFilesService, IGitItemContent } from "VSS/Features/Monaco/Contracts";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssThemeService } from "VSS/Platform/Theming";
import { ILanguageProvider, MonacoProviderProxy } from "./MonacoProviderProxy";

export interface IContributedMonacoLanguage {
    extensions?: string[];
    filenames?: string[];
    filenamePatterns?: string[];
    firstLine?: string;
    aliases?: string[];
    mimetypes?: string[];

    language?: monaco.languages.IMonarchLanguage;
    configuration?: monaco.languages.LanguageConfiguration;
}

interface IAzureDevOpsParsedFileInfo {
    repoId: string;
    ref: string;
    isCommit: boolean;
    relativeFilePath: string;
    commitId?: string;
}

interface IContributedJsonSchema {
    /**
     * The URI of the schema, which is also the identifier of the schema.
     */
    uri: string;

    /**
     * A list of file names that are associated to the schema. The '*' wildcard can be used. For example '*.schema.json', 'package.json'
     */
    fileMatch?: string[];

    /**
     * The schema for the given URI.
     * See https://github.com/Microsoft/vscode-json-languageservice/blob/master/src/jsonSchema.ts
     */
    schema?: any;
}

interface IContributedLanguageProvider {
    availableLanguages: string[];
    providers: ILanguageProvider;
    getFileCapabilities?(filePath: string, ref?: string, isCommit?: boolean): Promise<{ [key: string]: boolean } | null>;
    getKnownIdentifiers?(filePath: string, ref?: string, isCommit?: boolean): Promise<monaco.IRange[]>;
    getCodeLensContext?(): monaco.languages.CodeLensList;
}

type ResourceChangedCallback = (resource: IResourceChangedArgs) => void;

interface IResourceChangedArgs {
    uri: string;
    getContent: () => string;
    languageId: string;
}

interface IJsonSchemaProviderContext {
    /**
     * Get the list of currently registered JSON schemas.
     */
    getJsonSchemas: () => IContributedJsonSchema[];

    /**
     * Register a callback to be informed when a change happens to the current resource:
     *  - a new file is loaded
     *  - the user edits the file in the web UI
     *  - the detected language changes (ie after an extension registered a new language, and the file was detected to have that language)
     */
    onResourceChanged: (callback: ResourceChangedCallback) => void;

    /**
     * Register schemas with the code editor's built-in JSON language
     */
    registerJsonSchemas: (schemas: IContributedJsonSchema[]) => void;
}

interface IContributedJsonSchemaProviderService {
    register(context: IJsonSchemaProviderContext): void;
}

// Helper method to allow regular expressions to be specified in JSON
// through the form { _regex: "expression", _flags: "g" }
function replaceRegExp(obj: any): any {
    if (obj === null || obj === undefined) {
        return obj;
    } else if (obj instanceof Array) {
        return obj.map(replaceRegExp);
    } else if (typeof obj === "object") {
        if (typeof obj._regex === "string") {
            return new RegExp(obj._regex, typeof obj._flags === "string" ? obj._flags : undefined);
        } else {
            for (const key in obj) {
                obj[key] = replaceRegExp(obj[key]);
            }
        }
    }
    return obj;
}

const languageContributionType = "ms.vss-features.code-editor-language";
const jsonSchemaContributionType = "ms.vss-features.code-editor-json-schema";
const jsonSchemaProviderContributionType = "ms.vss-features.code-editor-json-schema-provider";
const languageProviderContributionType = "ms.vss-features.code-editor-navigation-provider";

interface IMonacoLanguageExtensionService extends IVssService {
    registerLanguages(): void;
    registerJsonSchemas(): void;
    registerLanguageProviders(fileExtension?: string, fileUrl?: string, disposables?: monaco.IDisposable[], delayedHighlighting?: (ranges: monaco.IRange[], theme: string) => void, theme?: string): void;
}

class MonacoLanguageExtensionService extends VssService implements IMonacoLanguageExtensionService {
    private registeredContributionIds = new Set<string>();
    private resourceChangedCallbacks: ResourceChangedCallback[] = [];

    public registerLanguages(): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const languageContributions = contributionService.getContributionsByType<IContributedMonacoLanguage>(languageContributionType);
        for (const languageContributionId in languageContributions) {
            if (!this.registeredContributionIds.has(languageContributionId)) {
                this.registeredContributionIds.add(languageContributionId);
                this.registerLanguage(languageContributionId, languageContributions[languageContributionId]);
            }
        }
    }

    public registerJsonSchemas(): void {
        // Fixed set of schemas
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const jsonSchemaContributions = contributionService.getContributionsByType<IContributedJsonSchema>(jsonSchemaContributionType);
        for (const schemaContributionId in jsonSchemaContributions) {
            if (!this.registeredContributionIds.has(schemaContributionId)) {
                this.registeredContributionIds.add(schemaContributionId);
                this.registerJsonSchema(schemaContributionId, jsonSchemaContributions[schemaContributionId]);
            }
        }

        // Dynamic schema providers
        const providerContributions = contributionService.getContributionsByType(jsonSchemaProviderContributionType);
        for (const providerContributionId in providerContributions) {
            if (!this.registeredContributionIds.has(providerContributionId)) {
                this.registeredContributionIds.add(providerContributionId);
                this.loadDynamicSchemaProvider(providerContributionId);
            }
        }
    }

    public async registerLanguageProviders(fileExtension?: string, filePath?: string, disposables?: monaco.IDisposable[], delayedHighlighting?: (ranges: monaco.IRange[], theme: string) => void, theme?: string): Promise<void> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const providerContributions = contributionService.getContributionsByType(languageProviderContributionType);
        for (const providerContributionId in providerContributions) {
            if (!this.registeredContributionIds.has(providerContributionId)) {
                this.registeredContributionIds.add(providerContributionId);
            }
            const contribution = await contributionService.getService(providerContributionId);
            this.registerLanguageProvider(
                providerContributionId,
                contribution as IContributedLanguageProvider,
                fileExtension,
                filePath,
                disposables,
                delayedHighlighting,
                theme);
        }
    }

    private async getGitItem(resourceUriString: string): Promise<IGitItemContent | undefined> {
        // Example Resource URI strings - branch slashes are serialized
        // inmemory://model/git/19d6a1e0-25bf-4d20-98b6-f2cbf9222770/GBmain/src/CloudKernel.InternalApi/Controllers/ControllerBase.cs
        // inmemory://model/git/19d6a1e0-25bf-4d20-98b6-f2cbf9222770/GBmohaimen%2Ftest%2Fbranch/src/CloudKernel.InternalApi/Controllers/ControllerBase.cs

        let path = null;
        let branch = null;

        const [, parseFullFileUri] = resourceUriString.split(/GB(.+)/);
        let gitItem: IGitItemContent | undefined = undefined;
        if (parseFullFileUri) {
            [, branch, path] = parseFullFileUri.split(/([^\/]+)\/(.+)/);
            if (branch && path) {
                const gitFilesService = this.pageContext?.getService<IGitFilesService>("IGitFilesService");
                let version = "";
                let versionType = 0;
                try {
                    const filesService = this.pageContext?.getService<IFilesHubViewService>("IFilesHubViewService");
                    version = filesService!.getVersion().version;
                    versionType = filesService!.getVersion().versionType;
                }
                catch {
                    console.log('File service not available for the file - Indexing failure');
                    return;
                }
                finally {
                    gitItem = await gitFilesService.getItemContentPromise(path, {
                        version: version,
                        versionOptions: 0,
                        versionType: versionType
                    });

                    return gitItem;
                }
            }
        }
        console.log('Failed to parse the file, Indexing will not be available');
        return;
    }

    private registerLanguage(contributionId: string, contribution: IContributedMonacoLanguage): void {
        try {
            monaco.languages.register({
                id: contributionId,
                aliases: contribution.aliases,
                extensions: contribution.extensions,
                filenamePatterns: contribution.filenamePatterns,
                filenames: contribution.filenames,
                firstLine: contribution.firstLine,
                mimetypes: contribution.mimetypes
            });

            monaco.languages.onLanguage(contributionId, () => {
                if (contribution.configuration) {
                    monaco.languages.setLanguageConfiguration(contributionId, replaceRegExp(contribution.configuration));
                }
                if (contribution.language) {
                    monaco.languages.setMonarchTokensProvider(contributionId, replaceRegExp(contribution.language));
                }
            });
        } catch (ex) {
            console.error(`Failed to register monaco language contribution "${contributionId}". Exception: ${ex && ex.message}`);
        }
    }

    private registerJsonSchema(contributionId: string, contribution: IContributedJsonSchema): void {
        try {
            const jsonDefaults = monaco.languages.json.jsonDefaults;
            jsonDefaults.setDiagnosticsOptions({
                ...jsonDefaults.diagnosticsOptions,
                schemas: [...(jsonDefaults.diagnosticsOptions.schemas || []).filter(s => s.uri !== contribution.uri), contribution]
            });
        } catch (ex) {
            console.error(`Failed to register json schema contribution "${contributionId}". Exception: ${ex && ex.message}`);
        }
    }

    private async registerLanguageProvider(contributionId: string, contribution: IContributedLanguageProvider, fileExtension?: string, fileUrl?: string, disposables?: monaco.IDisposable[], delayedHighlighting?: (ranges: monaco.IRange[], theme: string) => void, theme?: string): Promise<void> {
        const languageByExt: { [key: string]: string } = {
            "cpp": "cpp",
            "cs": "csharp",
            "vb": "csharp",
            "h": "cpp",
            "java": "java",
            "js": "javascript",
            "jsx": "javascript",
            "rs": "rust",
            "ts": "typescript",
            "tsx": "typescript",
        };


        if (fileExtension && fileUrl && contribution.availableLanguages.indexOf(languageByExt[fileExtension]) != -1 && contribution.getFileCapabilities) {
            try {
                const fileInfo: IAzureDevOpsParsedFileInfo = await this.parseFileUrl(fileUrl);
                // By parsing ADO file information, all files will have an associated commitId
                // To-Do: Update endpoints so that isCommit do not need to be passed
                const capabilities = await contribution.getFileCapabilities(fileInfo.relativeFilePath, fileInfo.commitId, true);
                if (!capabilities) {
                    // if no capabilities are returned then don't make any other extension calls
                    return;
                }
                const codeLensContext = contribution.getCodeLensContext ? contribution.getCodeLensContext() : undefined;
                const languageId = languageByExt[fileExtension];
                const monacoProxyProvider = new MonacoProviderProxy(this.pageContext, contribution.providers, fileInfo.commitId);
                monacoProxyProvider.registerProviders(languageId, capabilities, codeLensContext, disposables);
                if (contribution.getKnownIdentifiers && delayedHighlighting) {
                    const identifiers = await contribution.getKnownIdentifiers(fileInfo.relativeFilePath, fileInfo.commitId, true);
                    monacoProxyProvider.addDiscoverability(delayedHighlighting, identifiers, theme!);
                }
            } catch (ex) {
                console.error(`Failed to register language providers contribution "${contributionId}". Exception: ${ex && ex.message}`);
            }
        }
    }

    private async loadDynamicSchemaProvider(contributionId: string): Promise<void> {
        try {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            const providerService = await contributionService.getService<IContributedJsonSchemaProviderService>(contributionId);
            if (providerService) {
                providerService.register({
                    getJsonSchemas: () => {
                        return monaco.languages.json.jsonDefaults.diagnosticsOptions.schemas || [];
                    },
                    onResourceChanged: (callback: ResourceChangedCallback): void => {
                        this.addResourceChangedCallback(callback);
                    },
                    registerJsonSchemas: (schemas: IContributedJsonSchema[]): void => {
                        for (const schema of schemas) {
                            this.registerJsonSchema(contributionId, schema);
                        }
                    }
                });
            }
        } catch (ex) {
            console.error(`Failed to load json schema provider "${contributionId}". Exception: ${ex && ex.message}`);
        }
    }

    private addResourceChangedCallback(callback: ResourceChangedCallback): void {
        this.resourceChangedCallbacks.push(callback);

        if (this.resourceChangedCallbacks.length === 1) {
            monaco.editor.onDidChangeModelLanguage(({ model }) => this.triggerResourceChangedCallbacks(model));
            monaco.editor.onDidCreateModel(model => {
                this.triggerResourceChangedCallbacks(model);
                model.onDidChangeContent(() => this.triggerResourceChangedCallbacks(model));
            });
        }

        // Invoke callback immediately for any existing models
        for (const model of monaco.editor.getModels()) {
            this.triggerResourceChangedCallback(model, callback);
        }
    }

    private triggerResourceChangedCallbacks(model: monaco.editor.ITextModel): void {
        for (const callback of this.resourceChangedCallbacks) {
            this.triggerResourceChangedCallback(model, callback);
        }
    }

    private triggerResourceChangedCallback(model: monaco.editor.ITextModel, callback: ResourceChangedCallback): void {
        callback({
            uri: model.uri.toString(),
            getContent: () => model.getValue(),
            languageId: model.getModeId()
        });
    }

    private async parseFileUrl(fileUri: string): Promise<IAzureDevOpsParsedFileInfo> {
        // Example File URI strings - branch slashes are serialized
        // inmemory://model/git/19d6a1e0-25bf-4d20-98b6-f2cbf9222770/GBmain/src/CloudKernel.InternalApi/Controllers/ControllerBase.cs
        // inmemory://model/git/19d6a1e0-25bf-4d20-98b6-f2cbf9222770/GBmohaimen%2Ftest%2Fbranch/src/CloudKernel.InternalApi/Controllers/ControllerBase.cs

        const pathComponents = fileUri.split("/");
        const repoId = pathComponents[4];
        const ref = pathComponents[5];
        const branchOrCommit = ref.slice(2);
        const filePath = pathComponents.slice(6).join("/");

        // ref starts with GB or GC: GB indicates branch, GC indicates commit
        // if the commit Id is already in the file URI then there is no need to call into Git files service
        const isCommit = ref[1] == "C";
        let commitId: string | undefined;

        if (isCommit) {
            commitId = branchOrCommit;
        }
        else {
            const gitContent = await this.getGitItem(fileUri);
            commitId = gitContent?.commitId;
        }
        return {
            repoId: repoId,
            ref: branchOrCommit,
            isCommit: isCommit,
            relativeFilePath: filePath,
            commitId: commitId,
        };
    }
}

Services.add("IMonacoLanguageExtensionService", { serviceFactory: MonacoLanguageExtensionService });

export function registerLanguageExtensions(pageContext: IVssPageContext, fileExtension?: string, fileUrl?: string, disposables?: monaco.IDisposable[], delayedHighlighting?: any): void {
    const languageService = pageContext.getService<IMonacoLanguageExtensionService>("IMonacoLanguageExtensionService");
    const themeService = pageContext.getService<IVssThemeService>("IVssThemeService");
    const monacoTheme = themeService.getCurrentTheme().isDark ? "vs-dark" : "vs";
    languageService.registerLanguages();
    languageService.registerJsonSchemas();
    languageService.registerLanguageProviders(fileExtension, fileUrl, disposables, delayedHighlighting, monacoTheme);
}